import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { setUserPassword } from '@/api/users.api';
import { ApiError } from '@/api/errors';
import { showErrorToast } from '@/lib/toast';
import './ResetPasswordDialog.css';

interface ResetPasswordDialogProps {
  /** `null` while closed — the target user's id + display name while open. */
  target: { id: string; fullName: string } | null;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

/**
 * Small accessible modal (same `role="dialog"` / focus-trap / Esc-to-close contract as
 * `ConfirmDialog`, but with an input field so it can't reuse that component directly) an Admin
 * uses to set a new password for *another* user — `PATCH /users/{id}/password` with no
 * `currentPassword` (that field is only required when a user changes their own password, which
 * this Admin-only screen never does for the signed-in Admin's own row — see `UsersPage`).
 */
export function ResetPasswordDialog({ target, onClose, onSuccess }: ResetPasswordDialogProps) {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const open = Boolean(target);

  useEffect(() => {
    if (!open) return;

    setNewPassword('');
    setShowPassword(false);
    setError(null);
    setBanner(null);
    setSubmitting(false);

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused.current?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || !target) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBanner(null);

    if (!newPassword) {
      setError(t('usersSettings.resetPassword.newPasswordRequired'));
      inputRef.current?.focus();
      return;
    }
    if (newPassword.length < 8) {
      setError(t('usersSettings.resetPassword.newPasswordTooShort'));
      inputRef.current?.focus();
      return;
    }
    setError(null);

    setSubmitting(true);
    try {
      await setUserPassword(target!.id, { newPassword });
      onSuccess(t('usersSettings.resetPassword.success', { name: target!.fullName }));
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      const message =
        apiError.code === 'NETWORK_ERROR'
          ? t('usersSettings.resetPassword.networkError')
          : apiError.message || t('usersSettings.resetPassword.genericError');
      setBanner(message);
      if (apiError.code === 'NETWORK_ERROR') showErrorToast(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fa-dialog-overlay">
      <div
        className="fa-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fa-reset-password-title"
        ref={dialogRef}
      >
        <h2 id="fa-reset-password-title" className="fa-dialog__title">
          {t('usersSettings.resetPassword.title', { name: target.fullName })}
        </h2>
        <p className="fa-dialog__body">{t('usersSettings.resetPassword.description')}</p>

        {banner && (
          <p className="fa-field__error" role="alert">
            {banner}
          </p>
        )}

        <form noValidate onSubmit={handleSubmit}>
          <div className="fa-field">
            <label htmlFor="reset-password-new">
              {t('usersSettings.resetPassword.newPassword')}
              <span className="fa-field__required" aria-hidden="true">
                {' '}
                *
              </span>
            </label>
            <div className="fa-reset-password__row">
              <input
                id="reset-password-new"
                ref={inputRef}
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className="fa-ltr-isolate"
                value={newPassword}
                disabled={submitting}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? 'reset-password-new-error' : undefined}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setError(null);
                }}
              />
              <button
                type="button"
                className="fa-reset-password__toggle"
                aria-pressed={showPassword}
                aria-label={
                  showPassword
                    ? t('usersSettings.resetPassword.hidePassword')
                    : t('usersSettings.resetPassword.showPassword')
                }
                onClick={() => setShowPassword((v) => !v)}
                disabled={submitting}
              >
                {showPassword ? '\u{1F648}' : '\u{1F441}'}
              </button>
            </div>
            {error && (
              <p id="reset-password-new-error" className="fa-field__error">
                {error}
              </p>
            )}
          </div>

          <div className="fa-dialog__actions">
            <button
              type="button"
              className="fa-btn fa-btn--secondary"
              disabled={submitting}
              onClick={onClose}
            >
              {t('usersSettings.resetPassword.cancelButton')}
            </button>
            <button type="submit" className="fa-btn fa-btn--primary" disabled={submitting}>
              {submitting
                ? t('usersSettings.resetPassword.submittingButton')
                : t('usersSettings.resetPassword.submitButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
